import questionnaireImportSchema from '../schemas/questionnaire-import.schema.json';

// JSON Schema (draft 2020-12) describing the import format. Exposed so the UI
// can let users download it (e.g. to feed an AI generator).
export const QUESTIONNAIRE_IMPORT_SCHEMA = questionnaireImportSchema;

// Parses and validates questionnaire JSON for import into the editor.
//
// Accepts two shapes:
//   1. The clean "AI" schema (docs/questionnaire-import.schema.json): { title, pages[], ... }
//   2. The editor's own export envelope: { exportVersion, ..., questionnaire: {...} }
//
// Returns either:
//   { ok: true,  data, format, warnings }   // `data` is restoreFullState-shaped
//   { ok: false, errors }                    // human-readable German messages

// Ready-made prompt to paste into a chatbot so it generates questionnaire JSON
// in exactly the shape parseQuestionnaireImport() accepts.
export const AI_IMPORT_PROMPT = `ZUSÄTZLICHE AUSGABE-VORGABE (Format, nicht Inhalt):

Den gewünschten Inhalt des Fragebogens (Thema, Stelle, Fragen, Texte) bestimmst du nach meiner obigen Anweisung. Gib dein Ergebnis AUSSCHLIESSLICH als gültiges JSON aus – kein Markdown, keine Code-Blöcke, kein Text davor oder danach – exakt in dieser Struktur:

{
  "title": "string",
  "introText": "string (optional)",
  "outroText": "string (optional)",
  "cancelText": "string (optional)",
  "cancelOutroText": "string (optional)",
  "autoSubmitEnabled": false,
  "allowMultipleSubmissions": false,
  "scoringTotalScore": 100,
  "scoringPrompt": "string (optional)",
  "introPage": {
    "welcomeText": "string (optional)",
    "overviewText": "string (optional, Text vor der Abschnittsliste)",
    "afterSectionsText": "string (optional, Text nach der Abschnittsliste)",
    "sectionsText": "string (optional, Überschrift der Abschnittsliste)",
    "reviewSectionTitle": "string (optional, Überprüfung Titel)",
    "reviewSectionDescription": "string (optional, Überprüfung Beschreibung)",
    "timerText": "string (optional)",
    "startButtonText": "string (optional)"
  },
  "defaultQuestions": [ /* optional, gleiches Frageformat wie unten */ ],
  "pages": [
    {
      "title": "string",
      "description": "string (optional)",
      "questions": [
        {
          "type": "single-choice",
          "text": "string",
          "description": "string (optional)",
          "important": false,
          "maxScore": 0,
          "options": [
            { "label": "string", "score": 0 }
          ]
        }
      ]
    }
  ]
}

Struktur- & Scoring-Regeln (NICHT den Inhalt betreffend):
- Erlaubte "type"-Werte: text, single-choice, multiple-choice, ranking, combobox, matrix, image, file-upload.
- "title" und "pages" sind Pflicht; jede Seite braucht "title" und "questions"; jede Frage braucht "type" und "text".
- "options" ist erforderlich für single-choice, multiple-choice, ranking und combobox; bei anderen Typen weglassen.
- Scoring: "maxScore" = maximale Punkte pro Frage; "score" je Option nur bei single-choice / multiple-choice; bei ranking/combobox die Optionen ohne "score". Die Summe der "maxScore" sollte zu "scoringTotalScore" passen.
- Verwende ausschließlich die oben gezeigten Felder.
- Gib am Ende NUR das JSON-Objekt zurück.`;

export const IMPORT_QUESTION_TYPES = [
  'text',
  'single-choice',
  'multiple-choice',
  'ranking',
  'combobox',
  'matrix',
  'image',
  'file-upload'
];

// Types that require at least one option to make sense.
const OPTION_TYPES = ['single-choice', 'multiple-choice', 'ranking', 'combobox'];

const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

// Maps one imported question into the editor's question shape.
// Pushes any validation problems onto `errors` using the given JSON path.
function mapQuestion(q, pageId, errors, path) {
  if (!isPlainObject(q)) {
    errors.push(`${path}: muss ein Objekt sein.`);
    return null;
  }
  if (!q.type || !IMPORT_QUESTION_TYPES.includes(q.type)) {
    errors.push(`${path}.type: ungültiger Typ "${q.type ?? ''}". Erlaubt: ${IMPORT_QUESTION_TYPES.join(', ')}.`);
  }
  if (typeof q.text !== 'string' || !q.text.trim()) {
    errors.push(`${path}.text: ist erforderlich.`);
  }

  // Options can be objects ({label, score}) or plain strings.
  let options = [];
  let optionScores;
  if (Array.isArray(q.options)) {
    options = q.options.map((o) => (typeof o === 'string' ? o : String(o?.label ?? '')));
    const hasScores = q.options.some((o) => isPlainObject(o) && o.score !== undefined && o.score !== null);
    if (hasScores) {
      optionScores = q.options.map((o) => (isPlainObject(o) ? Number(o.score) || 0 : 0));
    }
  }
  if (OPTION_TYPES.includes(q.type) && options.length === 0) {
    errors.push(`${path}.options: für Typ "${q.type}" ist mindestens eine Option erforderlich.`);
  }

  const mapped = {
    type: q.type,
    text: typeof q.text === 'string' ? q.text : '',
    description: typeof q.description === 'string' ? q.description : '',
    important: !!q.important,
    maxScore: Number(q.maxScore) || 0,
    options
  };
  if (optionScores) mapped.optionScores = optionScores;
  if (pageId) mapped.pageId = pageId;
  return mapped;
}

// Maps the clean AI schema into restoreFullState-shaped editor data.
function mapAiSchema(obj, warnings) {
  const errors = [];

  if (typeof obj.title !== 'string' || !obj.title.trim()) {
    errors.push('title: ist erforderlich (nicht-leerer Text).');
  }
  if (!Array.isArray(obj.pages) || obj.pages.length === 0) {
    errors.push('pages: ist erforderlich (nicht-leeres Array).');
  }

  const questionPages = [];
  const questions = [];

  if (Array.isArray(obj.pages)) {
    obj.pages.forEach((page, pi) => {
      if (!isPlainObject(page)) {
        errors.push(`pages[${pi}]: muss ein Objekt sein.`);
        return;
      }
      // Deterministic-ish unique id; index keeps it stable within one import.
      const pageId = `imported_page_${pi}_${Math.random().toString(36).slice(2, 8)}`;
      questionPages.push({
        id: pageId,
        title: (typeof page.title === 'string' && page.title.trim()) ? page.title : `Seite ${pi + 1}`,
        description: typeof page.description === 'string' ? page.description : '',
        order: pi + 1,
        pageType: 'custom'
      });

      const qs = Array.isArray(page.questions) ? page.questions : [];
      if (qs.length === 0) {
        warnings.push(`pages[${pi}] ("${page.title || pi + 1}") enthält keine Fragen.`);
      }
      qs.forEach((q, qi) => {
        const mapped = mapQuestion(q, pageId, errors, `pages[${pi}].questions[${qi}]`);
        if (mapped) questions.push(mapped);
      });
    });
  }

  const defaultQuestions = [];
  if (Array.isArray(obj.defaultQuestions)) {
    obj.defaultQuestions.forEach((q, qi) => {
      const mapped = mapQuestion(q, null, errors, `defaultQuestions[${qi}]`);
      if (mapped) defaultQuestions.push(mapped);
    });
  }

  if (errors.length) return { ok: false, errors };

  const data = {
    title: obj.title,
    questions,
    questionPages,
    defaultQuestions,
    introText: typeof obj.introText === 'string' ? obj.introText : '',
    outroText: typeof obj.outroText === 'string' ? obj.outroText : '',
    scoringTotalScore: Number(obj.scoringTotalScore) || 100,
    scoringPrompt: typeof obj.scoringPrompt === 'string' ? obj.scoringPrompt : ''
  };

  // Optional withdrawal ("Absage") texts.
  if (typeof obj.cancelText === 'string') data.cancelText = obj.cancelText;
  if (typeof obj.cancelOutroText === 'string') data.cancelOutroText = obj.cancelOutroText;

  // Optional intro page (Einführungsseite) config. Only attach keys that were
  // provided so unspecified fields keep their editor defaults.
  const ip = isPlainObject(obj.introPage) ? obj.introPage : null;
  if (ip || obj.autoSubmitEnabled !== undefined || obj.allowMultipleSubmissions !== undefined) {
    const introPage = {};
    [
      'welcomeText', 'overviewText', 'afterSectionsText', 'sectionsText',
      'defaultSectionTitle', 'defaultSectionDescription',
      'reviewSectionTitle', 'reviewSectionDescription',
      'timerText', 'startButtonText'
    ].forEach((k) => {
      if (ip && typeof ip[k] === 'string') introPage[k] = ip[k];
    });
    if (obj.autoSubmitEnabled !== undefined) introPage.autoSubmitEnabled = !!obj.autoSubmitEnabled;
    if (obj.allowMultipleSubmissions !== undefined) introPage.allowMultipleSubmissions = !!obj.allowMultipleSubmissions;
    data.intro_page = introPage;
  }

  return { ok: true, data, format: 'ai', warnings };
}

// Entry point. `raw` is an already-JSON.parsed value.
/**
 * Lässt importierte Fragen die ID behalten, die dieselbe Frage im Fragebogen
 * schon hatte.
 *
 * Ein Import vergibt sonst für jede Frage eine neue ID. Die Antworten
 * bestehender Bewerbungen sind aber nach Frage-ID geschlüsselt: mit neuen IDs
 * findet die Ergebnisse-Seite zu keiner Frage mehr eine Antwort, und ganze
 * Seiten wirken unbeantwortet (die Antworten sind da, siehe
 * utils/submissionAnswerRecovery.js — nur eben nicht mehr zugeordnet).
 *
 * Zugeordnet wird über den Fragetext, weil das das Einzige ist, was ein Import
 * und ein bestehender Fragebogen gemeinsam haben. Jede bestehende ID wird
 * höchstens einmal vergeben; bei doppeltem Text gewinnt die erste Frage.
 */
export function preserveQuestionIds(data, existingQuestions = []) {
  const normalize = (text) =>
    String(text || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const available = new Map();
  for (const question of Array.isArray(existingQuestions) ? existingQuestions : []) {
    const key = normalize(question?.text);
    if (!key || !question?.id) continue;
    if (!available.has(key)) available.set(key, []);
    available.get(key).push(question.id);
  }

  let kept = 0;
  let fresh = 0;
  const remap = (questions) =>
    (Array.isArray(questions) ? questions : []).map((question) => {
      const queue = available.get(normalize(question?.text));
      const reused = queue && queue.length ? queue.shift() : null;
      if (reused) {
        kept += 1;
        return { ...question, id: reused };
      }
      fresh += 1;
      return question;
    });

  const next = {
    ...data,
    questions: remap(data?.questions),
    defaultQuestions: remap(data?.defaultQuestions),
  };

  return { data: next, kept, fresh };
}

export function parseQuestionnaireImport(raw) {
  const warnings = [];

  if (!isPlainObject(raw)) {
    return { ok: false, errors: ['Die JSON-Wurzel muss ein Objekt sein.'] };
  }

  // Editor export envelope → the inner questionnaire is already editor-shaped.
  if (raw.questionnaire !== undefined || raw.exportVersion !== undefined) {
    if (!isPlainObject(raw.questionnaire)) {
      return { ok: false, errors: ['Export-Datei erkannt, aber das Feld "questionnaire" fehlt oder ist ungültig.'] };
    }
    if (typeof raw.questionnaire.title !== 'string' || !raw.questionnaire.title.trim()) {
      warnings.push('Export-Datei ohne Titel — bitte den Titel nach dem Import prüfen.');
    }
    return { ok: true, data: raw.questionnaire, format: 'export', warnings };
  }

  // Otherwise treat it as the clean AI schema.
  return mapAiSchema(raw, warnings);
}
